import { z } from 'zod';
import {
  getTransform,
  getTransformOutputType,
  isTransform,
  TRANSFORM_DEFINITIONS,
  type TransformDefinition,
} from '@erp/workflows';

/**
 * The functions the builder offers, derived from the transform registry so a
 * newly registered transform shows up without touching the editor. `collect`
 * is grammar rather than a registry entry, so it is described here.
 */
export interface BuilderFunction {
  /** The name written into the expression. */
  name: string;
  /** Short glyph for chips, blocks, and the canvas badge. */
  label: string;
  description?: string;
  /** Empty slots a new call starts with. */
  minArgs: number;
  variadic: boolean;
  /**
   * The socket type each argument should carry — `'any'` when the registry's
   * schema is richer than a single socket type. Index past the declared
   * tuple reads the variadic rest type.
   */
  argTypes: readonly string[];
  restArgType: string;
  /** The result's socket type, when it is a plain scalar. */
  outputType?: string;
  /**
   * Whether the function picker lists it. `get` is never a block — a field is
   * chosen inside its source's argument row — so it is parsed and shown, but
   * not offered.
   */
  pickable: boolean;
}

export const COLLECT_FUNCTION: BuilderFunction = {
  name: 'collect',
  label: '[ ]',
  description:
    'Gathers its inputs into one array, dropping any that are empty.',
  minArgs: 1,
  variadic: true,
  argTypes: ['any'],
  restArgType: 'any',
  outputType: undefined,
  pickable: true,
};

const SCALAR_SCHEMA_TYPES = new Set(['string', 'number', 'boolean']);

/** The slice of a Zod schema's internals that names its kind. */
interface SchemaWithDef {
  _zod: { def: { type: string } };
}

function socketTypeOfSchema(schema: SchemaWithDef | null | undefined): string {
  const type = schema?._zod.def.type;
  return type && SCALAR_SCHEMA_TYPES.has(type) ? type : 'any';
}

function fromTransform(transform: TransformDefinition): BuilderFunction {
  const { items, rest } = transform.args.def;
  return {
    name: transform.name,
    label: transform.label ?? transform.name,
    description: transform.description,
    minArgs: items.length,
    variadic: transform.variadic === true,
    argTypes: items.map((item) => socketTypeOfSchema(item)),
    restArgType: socketTypeOfSchema(rest),
    outputType: getTransformOutputType(transform.name),
    pickable: transform.name !== 'get',
  };
}

export const BUILDER_FUNCTIONS: readonly BuilderFunction[] = [
  ...TRANSFORM_DEFINITIONS.map(fromTransform),
  COLLECT_FUNCTION,
];

const FUNCTIONS_BY_NAME = new Map(
  BUILDER_FUNCTIONS.map((definition) => [definition.name, definition]),
);

export function getBuilderFunction(name: string): BuilderFunction | undefined {
  return FUNCTIONS_BY_NAME.get(name);
}

/** The functions the picker lists, in registry order. */
export const PICKABLE_FUNCTIONS = BUILDER_FUNCTIONS.filter(
  (definition) => definition.pickable,
);

/** The short glyph for a call name, falling back to the name itself. */
export function functionLabel(name: string): string {
  return getBuilderFunction(name)?.label ?? name;
}

/** The socket type argument `index` of `fn` expects. */
export function argumentType(fn: string, index: number): string {
  const definition = getBuilderFunction(fn);
  if (!definition) return 'any';
  return definition.argTypes[index] ?? definition.restArgType;
}

/**
 * Why `value` cannot be argument `index` of `fn`, or `null` when it can —
 * checked against the transform's own argument schema, so a literal the
 * engine would reject (a negative `substring` start) is caught while typing.
 * `collect` and unknown functions take anything.
 */
export function literalArgumentError(
  fn: string,
  index: number,
  value: string | number,
): string | null {
  if (!isTransform(fn)) return null;
  const { items, rest } = getTransform(fn).args.def;
  const schema = items[index] ?? rest;
  if (!schema) return null;
  const result = z.safeParse(schema, value);
  return result.success ? null : (result.error.issues[0]?.message ?? null);
}
